/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2026 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2026 - Finnish Meteorological Institute (FMI)
 * Copyright 2026 - The Norwegian Meteorological Institute (MET Norway)
 * */

import { useCallback, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import type {
  AnimationState,
  NotificationScopeEvent,
  PluginManager,
  ScopeEventMap,
  ScopeName,
  TimeState,
} from '@opengeoweb/plugin-interface';
import { snackbarActions, snackbarTypes } from '@opengeoweb/snackbar';
import type { CoreAppStore, syncGroupsTypes } from '@opengeoweb/store';
import {
  genericActions,
  genericSelectors,
  mapActions,
  mapSelectors,
  syncConstants,
} from '@opengeoweb/store';
import type { IsEventRoundTrip } from '../hooks';
import { useDispatchOnScopeChange, useSendOnReduxChange } from '../hooks';

interface UsePluginReduxBridgeOptions {
  pluginBridge: PluginManager;
  sourceId: string;
  scopes?: string[];
}

const toSnackbarMessage = (
  event: NotificationScopeEvent,
): snackbarTypes.SnackbarMessage => {
  let prefix = '';
  if (event.severity === 'ERROR') {
    prefix = 'Error: ';
  } else if (event.severity === 'WARNING') {
    prefix = 'Warning: ';
  }

  return {
    type: snackbarTypes.SnackbarMessageType.VERBATIM_MESSAGE,
    message: `${prefix}${event.content}`,
  };
};

export const usePluginReduxBridge = ({
  pluginBridge,
  sourceId,
  scopes,
}: UsePluginReduxBridgeOptions): void => {
  const dispatch = useDispatch();

  const hasScope = useCallback(
    (scope: ScopeName): boolean => (!scopes ? true : scopes.includes(scope)),
    [scopes],
  );
  const syncGroupsAddSource = useCallback(
    (payload: syncGroupsTypes.SyncGroupsAddSourcePayload): void => {
      dispatch(genericActions.syncGroupAddSource(payload));
    },
    [dispatch],
  );
  const syncGroupRemoveSource = useCallback(
    (payload: syncGroupsTypes.SyncGroupRemoveSourcePayload): void => {
      dispatch(genericActions.syncGroupRemoveSource(payload));
    },
    [dispatch],
  );

  useEffect(() => {
    if (hasScope('time')) {
      syncGroupsAddSource({
        id: sourceId,
        type: [syncConstants.SYNCGROUPS_TYPE_SETTIME],
      });
    }

    return (): void => {
      syncGroupRemoveSource({ id: sourceId });
    };
  }, [syncGroupsAddSource, syncGroupRemoveSource, sourceId, hasScope]);

  const timeValue = useSelector((store: CoreAppStore) =>
    genericSelectors.getTime(
      genericSelectors.getSynchronizationGroupStore(store),
      sourceId,
    ),
  );
  const animationStartTime = useSelector((store: CoreAppStore) =>
    mapSelectors.getAnimationStartTime(store, sourceId),
  );
  const animationEndTime = useSelector((store: CoreAppStore) =>
    mapSelectors.getAnimationEndTime(store, sourceId),
  );
  const animationState = useMemo(
    () => ({ start: animationStartTime, end: animationEndTime }),
    [animationStartTime, animationEndTime],
  );

  // ============ REDUX -> PLUGIN ============
  const toTimeEvent = useCallback(
    (time: typeof timeValue) =>
      time?.currentTime
        ? {
            type: 'setTime' as const,
            time: time.currentTime.replace(/\.\d{3}Z$/, 'Z'),
          }
        : null,
    [],
  );

  const toAnimationEvent = useCallback(
    (anim: { start: string | undefined; end: string | undefined }) =>
      anim.start !== undefined && anim.end !== undefined
        ? {
            type: 'setAnimationTimeSpan' as const,
            startTime: anim.start,
            endTime: anim.end,
          }
        : null,
    [],
  );

  // Return true when a Redux event already matches the bridge state (plugin echo), preventing plugin → Redux → plugin loops without blocking real external updates.
  const isTimeEventRoundTrip = useCallback<IsEventRoundTrip<'time'>>(
    (event: ScopeEventMap['time'], bridgeState: TimeState | undefined) =>
      bridgeState?.time === event.time,
    [],
  );

  const isAnimationEventRoundTrip = useCallback<IsEventRoundTrip<'animation'>>(
    (
      event: ScopeEventMap['animation'],
      bridgeState: AnimationState | undefined,
    ) => {
      if (!bridgeState) {
        return false;
      }
      switch (event.type) {
        case 'setAnimationStartTime':
          return bridgeState.animationStartTime === event.time;
        case 'setAnimationEndTime':
          return bridgeState.animationEndTime === event.time;
        case 'setAnimationTimeSpan':
          return (
            bridgeState.animationStartTime === event.startTime &&
            bridgeState.animationEndTime === event.endTime
          );
        default:
          return false;
      }
    },
    [],
  );

  useSendOnReduxChange(
    pluginBridge,
    'time',
    timeValue,
    toTimeEvent,
    hasScope('time'),
    isTimeEventRoundTrip,
  );
  useSendOnReduxChange(
    pluginBridge,
    'animation',
    animationState,
    toAnimationEvent,
    hasScope('animation'),
    isAnimationEventRoundTrip,
  );

  // ============ PLUGIN -> REDUX ============
  const timeActionCreator = useCallback(
    (timeState: { time: string }) =>
      genericActions.setTime({
        sourceId,
        origin: 'PluginBridge',
        value: { currentTime: timeState.time },
      }),
    [sourceId],
  );

  const animationActionCreator = useCallback(
    (animState: { animationStartTime: string; animationEndTime: string }) => [
      mapActions.setAnimationStartTime({
        mapId: sourceId,
        animationStartTime: animState.animationStartTime,
      }),
      mapActions.setAnimationEndTime({
        mapId: sourceId,
        animationEndTime: animState.animationEndTime,
      }),
    ],
    [sourceId],
  );

  const snackbarActionCreator = useCallback((state: NotificationScopeEvent) => {
    if (state.type !== 'showNotification') {
      return null;
    }
    return snackbarActions.openSnackbar(toSnackbarMessage(state));
  }, []);

  useDispatchOnScopeChange(
    pluginBridge,
    'time',
    timeActionCreator,
    hasScope('time'),
  );

  useDispatchOnScopeChange(
    pluginBridge,
    'animation',
    animationActionCreator,
    hasScope('animation'),
  );

  useDispatchOnScopeChange(
    pluginBridge,
    'notification',
    snackbarActionCreator,
    hasScope('notification'),
  );
};
